import { Button, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';

import { columns } from "./grid-columns";
import { SiTrackPage } from "../../../components/si-track-page";
import { SiTrackGrid } from "../../../components/si-track-grid/si-track-grid";
import { contactApiKeyStore } from "../../../api/contact-api-key-store";
import { GuardedComponent } from "../../../components/guarded-component";
import { UserPermissionName } from "../../../models/user-permission-name";
import { useState } from "react";
import { MoveContactDialog } from "./move-contact/move-contact.dialog";
import { ViewMapContactDialog } from "./view-mapping-contact.dialog";
import { useLoadData } from "../../../hooks/useLoadData";
import { ApiResponse } from "../../../models/api-response";
import { Contact } from "../../../models/contact";
import { SaveHookFormDialog } from "../../../components/save-hook-form.dialog";
import { ContactForm } from "./create-contact.form";
import { contactFormSchema } from "./contact-form.schema";
import { SaveContactDialog } from "./save-contact.dialog";

const styling = {
    paperStyle: { padding: '20px', borderRadius: '20px' },
};

export const ContactsPage = () => {

    const isMoveContactEnabled = process.env.REACT_APP_MOVE_CONTACT_FLAG === 'true';

    const [contactId, setContactID] = useState<number>(0);
    const [customerID, setCustomerID] = useState<number>(0);
    const [emailReports, setEmailReports] = useState<any>(null);
    const [openDialog, setDialogAction] = useState<boolean>(false);
    const [openViewDialog, setViewDialogAction] = useState<boolean>(false);
    const [createItem, setCreateItem] = useState<any>();
    const [editItem, setEditItem] = useState<Partial<any>>();

    const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(contactApiKeyStore.getContacts());

    const handleViewLeave = (contactData: any) => {
        setContactID(contactData?.contactsID ?? 0);
        setViewDialogAction(!openViewDialog);
    }

    const handleMoveContact = (contactDetails: any) => {
        setContactID(contactDetails?.contactsID ?? 0);
        setCustomerID(contactDetails?.customerID ?? 0)
        setEmailReports({
            ppiReportStatusText: contactDetails?.ppiReportStatusText,
            susReportStatusText: contactDetails?.susReportStatusText,
            otherReportStatusText: contactDetails?.otherReportStatusText,
            contactsNotificationText: contactDetails?.contactsNotificationText
        })
        setDialogAction(true);
    }

    const handleEditContact = (contact: any) => {
        setEditItem({
            ...contact,
            // isMasterContacts: false,
            customer: { customerID: contact?.customerID ?? '', customerName: '' },
            contactRole: { contactsRoleID: contact?.contactsRoleID ?? '', contactsRoleName: "" },
        });
    };
    const handleCreateClose = () => {
        setCreateItem(undefined);
    };

    const handleEditClose = () => {
        setEditItem(undefined);
    };
    const handleCreateContact = () => {
        setCreateItem({ isActive: true, isMasterContacts: false });
    };

    const moveContactElement = (dataItem: any) => {
        return (
            <>
                {isMoveContactEnabled && (
                    <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MOVE_CONTACT]}>
                        <IconButton aria-label="move" onClick={() => handleMoveContact(dataItem?.dataItem)}>
                            <Tooltip title={<Typography variant="body1">Move</Typography>}>
                                <ArrowCircleRightOutlinedIcon style={{ color: '#E74C3C' }} />
                            </Tooltip>
                        </IconButton>
                    </GuardedComponent>
                )}
            </>
        );
    }

    const formatSubmitdata = (data: any) => {
        const contactsNotifications = data?.contactsNotifications?.map((item: any) => item.notificationValue).join(", ");
        return {
            ...data,
            customerID: data?.customer?.customerID,
            contactsRoleID: data?.contactRole?.contactsRoleID,
            contactsRoleName: data?.contactRole?.contactsRoleName,
            contactsNotificationIDs: data?.contactsNotificationIDs?.length > 0 ? data?.contactsNotificationIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
            ppiReportStatusIDs: data?.ppiReportStatusIDs?.length > 0 ? data?.ppiReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
            susReportStatusIDs: data?.susReportStatusIDs?.length > 0 ? data?.susReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
            otherReportStatusIDs: data?.otherReportStatusIDs?.length > 0 ? data?.otherReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
            contactMappingFrom: "mastercontact"
        };
    };

    return (
        <SiTrackPage
            title="Contacts"
            actionButton={
                <GuardedComponent requiredPermissions={[UserPermissionName.ADD_CONTACT]}>
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation={true}
                        startIcon={<AddIcon />}
                        onClick={handleCreateContact}
                    >
                        Add Contact
                    </Button>
                </GuardedComponent>
            }
        >
            <Paper elevation={0} style={styling.paperStyle}>
                <SiTrackGrid
                    fileTitle='master_contact'
                    columns={columns}
                    dataItemKey="contactsID"
                    dataItems={data?.data}
                    viewPopup={handleViewLeave}
                    onEdit={handleEditContact}
                    CustomComponent={moveContactElement}
                    deleteMutationConfig={(deleteItem) => contactApiKeyStore.deleteContact(deleteItem.contactsID)}
                    actionPermissions={{
                        edit: UserPermissionName.EDIT_CONTACT,
                        delete: UserPermissionName.DELETE_CONTACT,
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={isLoading || isFetching}
                />

                {createItem && (
                    <SaveHookFormDialog
                        title={createItem.contactsID ? 'UPDATE CONTACT' : 'CREATE CONTACT'}
                        config={{
                            saveConfig: createItem.contactsID
                                ? contactApiKeyStore.updateContact()
                                : contactApiKeyStore.createContact(),
                            invalidateRoute: () => contactApiKeyStore.getContacts(),
                        }}
                        data={createItem}
                        yupSchema={contactFormSchema}
                        formatSubmitdata={formatSubmitdata}
                        onClose={handleCreateClose}
                        size="xl"
                    >
                        <ContactForm />
                    </SaveHookFormDialog>
                )}

                {editItem && (
                    <SaveContactDialog
                        data={editItem}
                        onClose={handleEditClose}
                    />
                )}
                {openDialog && (
                    <MoveContactDialog
                        contactData={data?.data}
                        contactID={contactId}
                        customerID={customerID}
                        emailReports={emailReports}
                        onClosed={() => setDialogAction(false)} />
                )}
                {openViewDialog && (
                    <ViewMapContactDialog contactID={contactId} onClosed={() => setViewDialogAction(false)} />
                )}
            </Paper>
        </SiTrackPage>
    );
}